import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import PublicHeader from "@/components/PublicHeader";
import useSeo, { DEFAULT_TITLE, DEFAULT_DESC, DEFAULT_KEYWORDS } from "@/hooks/useSeo";
import {
  CheckCircle2,
  Stethoscope,
  ClipboardList,
  LineChart,
  Shield,
  Microscope,
  HeartPulse,
  Syringe,
  Leaf,
  Target,
  Handshake,
  Flame,
  Pill,
  Sparkles,
  Users,
  ChevronDown,
  MessageCircle,
  FileText,
  ShieldCheck,
  House,
} from "lucide-react";
import { api } from "@/lib/api";

const BRL = (v) => new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v || 0);

const HERO_IMG = "/images/hero-farmaceutico.jpg";

const AVALIACAO_BENEFITS = [
  { t: "Anamnese clínica completa", icon: ClipboardList },
  { t: "Avaliação de medicamentos e interações", icon: Pill },
  { t: "Análise de exames laboratoriais recentes", icon: Microscope },
  { t: "Orientação individualizada de saúde", icon: Handshake },
  { t: "Encaminhamento para plano de acompanhamento (opcional)", icon: FileText },
];

const AREAS_ACOMPANHAMENTO = [
  { t: "Metabolismo", d: "Peso, energia, glicemia, colesterol e composição corporal.", icon: Flame },
  { t: "Saúde Intestinal", d: "Digestão, disbiose, constipação, distensão e recorrências.", icon: Leaf },
  { t: "Farmacoterapia", d: "Uso correto, interações, adesão e revisão de medicamentos.", icon: Pill },
  { t: "Atendimento domiciliar e online", d: "Praticidade, agilidade no acesso ao cuidado e segurança no primeiro contato, sem o paciente sair de casa.", icon: House },
  { t: "Idosos Polimedicados", d: "Revisão farmacoterapêutica e otimização de longo prazo.", icon: Users },
  { t: "Suplementação e Vitaminas", d: "Ferro, B12, Vitamina D, magnésio e cofatores essenciais.", icon: Sparkles },
];

const PROCEDIMENTOS = [
  { t: "Aplicação de medicamentos injetáveis", d: "Intramusculares, subcutâneos e endovenosos com técnica farmacêutica.", icon: Syringe },
  { t: "Terapia vitamínica e mineral", d: "Reposição de vitamina D, B12, ferro e complexos personalizados.", icon: Sparkles },
  { t: "Análise clínica de exames", d: "Laudo comparativo com histórico e recomendações objetivas.", icon: Microscope },
  { t: "Monitoramento de sinais vitais", d: "Pressão, glicemia capilar e antropometria periódica.", icon: HeartPulse },
];

const FAQ = [
  {
    q: "Preciso ter uma doença para procurar o Centro?",
    a: "Não. O acompanhamento farmacêutico começa antes do sintoma virar problema — na prevenção, no ajuste da farmacoterapia e no monitoramento de metabolismo, intestino e adesão terapêutica.",
  },
  {
    q: "A Avaliação Farmacêutica de R$ 100 é obrigatória para assinar um plano?",
    a: "Recomendamos como primeira etapa. Ela permite construir um plano de cuidado individualizado antes de contratar Essencial ou Premium — mas não é obrigatória.",
  },
  {
    q: "Os procedimentos (injetáveis, terapia vitamínica) estão inclusos no plano mensal?",
    a: "Não. Procedimentos e aplicações são individualizados por indicação clínica e cobrados à parte, com valores transparentes agendáveis diretamente pelo app.",
  },
  {
    q: "Como funciona o acompanhamento contínuo?",
    a: "Você recebe protocolo por fases, faz check-ins semanais, envia exames pelo app e conta com um canal direto de dúvidas com o farmacêutico responsável.",
  },
  {
    q: "Posso cancelar o plano quando quiser?",
    a: "Sim. Assinatura mensal com renovação automática e cancelamento livre a qualquer momento — sem multa.",
  },
];

export default function LandingPage() {
  const [services, setServices] = useState([]);
  const [openFaq, setOpenFaq] = useState(0);
  useSeo({ title: DEFAULT_TITLE, description: DEFAULT_DESC, keywords: DEFAULT_KEYWORDS, path: "/" });

  useEffect(() => {
    api.get("/services?home_only=true").then((r) => setServices(r.data || [])).catch(() => setServices([]));
  }, []);

  const avaliacaoService = services.find((s) => (s.name || "").toLowerCase().includes("avaliação farmacêutica"));

  return (
    <div className="min-h-screen bg-background">
      <PublicHeader />

      {/* HERO */}
      <section className="relative bg-grain overflow-hidden">
        <div
          aria-hidden
          className="hidden lg:block absolute top-0 bottom-0 right-0 w-[42%] z-0"
          style={{ backgroundColor: "#EEF3EE" }}
        />
        <div className="relative z-10 max-w-7xl mx-auto px-6 pt-16 pb-24 md:pt-24 md:pb-32">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <div className="overline text-gold mb-6" data-testid="hero-overline">Centro Clínico Farmacêutico</div>
              <h1 className="brand-wordmark text-4xl sm:text-5xl lg:text-6xl font-bold text-primary leading-[1.05] tracking-tight uppercase">
                Centro de<br />
                <span className="text-accent">Performance</span><br />
                Farmacêutica
                <span className="sr-only"> — Farmacêutico Clínico em Tucuruí-PA</span>
              </h1>
              <p className="mt-6 text-lg text-primary/80 max-w-2xl leading-relaxed font-medium">
                Sua saúde não deve ser acompanhada apenas quando aparece um sintoma.
              </p>
              <p className="mt-4 text-base text-muted-foreground max-w-2xl leading-relaxed">
                Cuidado clínico farmacêutico contínuo — da avaliação inicial ao acompanhamento por
                planos mensais. Metabolismo, saúde intestinal, terapias injetáveis e monitoramento
                clínico com profissionais habilitados.
              </p>
              <div className="mt-10 flex flex-wrap items-center gap-4">
                <Link
                  to="/agendamento"
                  className="inline-flex items-center justify-center rounded-sm bg-primary text-primary-foreground px-6 py-3 text-sm font-semibold hover:bg-primary/90 transition-colors"
                  data-testid="hero-cta-avaliacao"
                >
                  Agendar Avaliação
                </Link>
                <a
                  href="#planos"
                  className="inline-flex items-center justify-center rounded-sm border border-gold text-primary px-6 py-3 text-sm font-semibold hover:bg-gold/10 transition-colors"
                  data-testid="hero-cta-plans"
                >
                  Ver Planos
                </a>
              </div>
              <div className="mt-10 flex flex-wrap items-center gap-6 text-sm text-muted-foreground">
                <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-accent" /> Farmacêuticos habilitados</div>
                <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-accent" /> Protocolo por fases</div>
                <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-accent" /> LGPD</div>
              </div>
            </div>
            <div className="lg:col-span-5">
              <div
                className="w-full h-[480px] lg:h-[640px] overflow-hidden rounded-lg flex items-center justify-center"
                style={{ backgroundColor: "#EEF3EE" }}
                data-testid="hero-image-wrapper"
              >
                <img
                  src={HERO_IMG}
                  alt="Farmacêutico clínico em Tucuruí-PA atendendo paciente pelo chat"
                  loading="eager"
                  fetchpriority="high"
                  decoding="async"
                  className="w-full h-full object-cover [object-position:50%_10%] lg:object-contain lg:[object-position:center_top] drop-shadow-[0_18px_28px_rgba(15,23,42,0.18)]"
                  data-testid="hero-image"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 1º — AVALIAÇÃO FARMACÊUTICA (R$ 100) */}
      <section id="avaliacao" className="py-24 md:py-28 bg-primary text-primary-foreground relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-5">
              <div className="overline text-gold mb-4">Ponto de partida</div>
              <h2 className="brand-wordmark text-3xl md:text-4xl font-bold uppercase leading-tight">
                Avaliação<br />
                <span className="text-gold">Farmacêutica</span>
              </h2>
              <div className="mt-6 flex items-baseline gap-2">
                <span className="brand-wordmark text-6xl font-bold text-gold">R$ 100</span>
                <span className="text-sm opacity-70">consulta única · 45 min</span>
              </div>
              <p className="mt-6 text-primary-foreground/85 leading-relaxed max-w-md">
                A porta de entrada do cuidado. Antes de qualquer plano ou protocolo, um farmacêutico
                habilitado analisa seu histórico, medicamentos e exames — e desenha o caminho clínico
                mais adequado para você.
              </p>
              <Link
                to="/agendamento"
                className="mt-8 inline-flex items-center justify-center rounded-sm bg-gold text-primary px-8 py-3.5 text-sm font-bold uppercase tracking-wider hover:bg-gold/90 transition-colors"
                data-testid="avaliacao-cta-agendar"
              >
                Agendar Avaliação
              </Link>
              {avaliacaoService && (
                <div className="mt-3 text-xs text-primary-foreground/60" data-testid="avaliacao-service-linked">
                  Serviço ativo · agendamento disponível
                </div>
              )}
            </div>
            <div className="lg:col-span-7">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {AVALIACAO_BENEFITS.map(({ t, icon: Icon }, i) => (
                  <div
                    key={t}
                    className="flex items-start gap-4 bg-primary-foreground/5 border border-gold/30 rounded-sm p-5 hover:border-gold transition-colors"
                    data-testid={`avaliacao-benefit-${i}`}
                  >
                    <div className="w-10 h-10 rounded-sm bg-gold/15 grid place-items-center flex-none">
                      <Icon className="w-5 h-5 text-gold" />
                    </div>
                    <div className="text-sm leading-relaxed pt-1.5">{t}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* O QUE VOCÊ PODE ACOMPANHAR */}
      <section id="acompanhamento" className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-3xl mb-14">
            <div className="overline text-gold mb-4">O que você pode acompanhar</div>
            <h2 className="brand-wordmark text-3xl md:text-5xl font-bold text-primary uppercase tracking-tight leading-[1.05]">
              Cuidado clínico<br /><span className="text-accent">em seis frentes.</span>
            </h2>
            <p className="mt-5 text-muted-foreground">
              Um único acompanhamento farmacêutico cobre as principais necessidades da sua saúde —
              sem sair da rotina, com dados objetivos e um profissional responsável.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {AREAS_ACOMPANHAMENTO.map(({ t, d, icon: Icon }) => (
              <div
                key={t}
                className="bg-card border border-border rounded-sm p-6 hover:border-gold transition-colors"
                data-testid={`area-${t.toLowerCase().replace(/\s/g, "-")}`}
              >
                <div className="w-11 h-11 rounded-sm bg-primary/5 border border-gold/40 grid place-items-center mb-5">
                  <Icon className="w-5 h-5 text-primary" />
                </div>
                <h3 className="font-heading font-semibold text-lg text-primary mb-2">{t}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 2º — PROCEDIMENTOS E APLICAÇÕES */}
      <section id="procedimentos" className="py-24 md:py-28 bg-secondary/40 border-y border-border">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-4">
              <div className="overline text-gold mb-4">Procedimentos e aplicações</div>
              <h2 className="brand-wordmark text-3xl md:text-4xl font-bold text-primary uppercase leading-tight">
                Definidos<br />
                <span className="text-accent">individualmente.</span>
              </h2>
              <p className="mt-5 text-muted-foreground leading-relaxed">
                Toda aplicação ou procedimento é indicado com base na Avaliação Farmacêutica e no
                acompanhamento clínico. Não estão inclusos nos planos mensais e são
                <strong className="text-primary"> cobrados à parte</strong>, com transparência de valores.
              </p>
              <div className="mt-6 flex items-start gap-3 text-xs text-muted-foreground bg-card border border-border rounded-sm p-4">
                <ShieldCheck className="w-4 h-4 text-accent mt-0.5 flex-none" />
                <span>Aplicações realizadas por farmacêuticos habilitados com registro ativo no CRF.</span>
              </div>
              <Link
                to="/agendamento"
                className="mt-8 inline-flex items-center justify-center rounded-sm border border-gold text-primary px-6 py-3 text-sm font-semibold hover:bg-gold/10 transition-colors"
                data-testid="procedimentos-cta-agendar"
              >
                Ver disponibilidade
              </Link>
            </div>
            <div className="lg:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {PROCEDIMENTOS.map(({ t, d, icon: Icon }, i) => (
                <div
                  key={t}
                  className="bg-card border border-border rounded-sm p-6 hover:border-gold transition-colors"
                  data-testid={`procedimento-${i}`}
                >
                  <Icon className="w-6 h-6 text-gold mb-4" />
                  <h3 className="font-heading font-semibold text-primary mb-2">{t}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{d}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* COMO FUNCIONA */}
      <section id="como-funciona" className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-3xl">
            <div className="overline text-gold mb-4">Metodologia clínica</div>
            <h2 className="brand-wordmark text-3xl md:text-5xl font-bold text-primary uppercase tracking-tight leading-[1.05]">
              Do primeiro check-in<br />à manutenção clínica.
            </h2>
          </div>
          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { n: "01", t: "Avaliação inicial", d: "Consulta farmacêutica de R$ 100 — anamnese, análise de medicamentos e exames.", icon: Stethoscope },
              { n: "02", t: "Plano de cuidado", d: "Definição de plano de acompanhamento (Essencial ou Premium) e procedimentos indicados.", icon: ClipboardList },
              { n: "03", t: "Check-ins semanais", d: "Score de sintomas, adesão e efeitos. Gráficos de evolução no app.", icon: LineChart },
              { n: "04", t: "Ajustes contínuos", d: "Reavaliação de exames, controle de aplicações e ajustes com o farmacêutico.", icon: Microscope },
            ].map(({ n, t, d, icon: Icon }) => (
              <div key={n} className="border border-border rounded-sm p-6 bg-card hover:shadow-sm transition-shadow" data-testid={`step-${n}`}>
                <div className="flex items-center justify-between mb-6">
                  <span className="overline text-muted-foreground">{n}</span>
                  <Icon className="w-5 h-5 text-primary" />
                </div>
                <h3 className="font-heading font-semibold text-lg mb-2">{t}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 3º — PLANOS DE ACOMPANHAMENTO */}
      <section id="planos" className="py-24 md:py-32 bg-primary text-primary-foreground">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <div className="overline text-gold mb-4">Planos de acompanhamento</div>
            <h2 className="brand-wordmark text-3xl md:text-5xl font-bold uppercase tracking-tight leading-[1.05]">
              Cuidado contínuo,<br /><span className="text-gold">não pontual.</span>
            </h2>
            <p className="mt-5 text-primary-foreground/70">
              Assinatura mensal com renovação automática. Cancele a qualquer momento.
            </p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {/* ESSENCIAL */}
            <div className="border border-gold/40 rounded-sm p-8 bg-primary-foreground/5" data-testid="plan-card-essencial">
              <div className="overline text-gold mb-3">Essencial</div>
              <div className="flex items-baseline gap-1">
                <span className="brand-wordmark text-5xl font-bold">R$ 97</span>
                <span className="text-primary-foreground/70">/mês</span>
              </div>
              <p className="mt-4 text-sm text-primary-foreground/75">Para quem precisa começar com estrutura clínica leve e consistente.</p>
              <ul className="mt-8 space-y-3 text-sm">
                {[
                  "Acompanhamento de uma frente clínica principal",
                  "Check-ins semanais e score de evolução",
                  "Canal de acompanhamento com farmacêutico",
                  "Painel do paciente com histórico e conteúdos",
                ].map((f) => (
                  <li key={f} className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-gold mt-0.5 flex-none" /> {f}
                  </li>
                ))}
              </ul>
              <Link
                to="/cadastro?plan=essencial"
                className="mt-8 inline-flex w-full items-center justify-center rounded-sm border border-gold text-primary-foreground px-4 py-3 text-sm font-semibold hover:bg-gold/10 transition-colors"
                data-testid="plan-cta-essencial"
              >
                Quero o Plano Essencial
              </Link>
            </div>

            {/* PREMIUM */}
            <div className="border-2 border-gold rounded-sm p-8 bg-primary-foreground/10 relative" data-testid="plan-card-premium">
              <div className="absolute -top-3 left-8 bg-gold text-primary text-xs font-bold px-3 py-1 rounded-sm tracking-wider uppercase">
                Recomendado
              </div>
              <div className="overline text-gold mb-3">Premium</div>
              <div className="flex items-baseline gap-1">
                <span className="brand-wordmark text-5xl font-bold">R$ 197</span>
                <span className="text-primary-foreground/70">/mês</span>
              </div>
              <p className="mt-4 text-sm text-primary-foreground/75">Cuidado clínico integral com dados, aplicações monitoradas e relatórios personalizados.</p>
              <ul className="mt-8 space-y-3 text-sm">
                {[
                  "Acompanhamento de múltiplas necessidades",
                  "Check-ins semanais e score de evolução",
                  "Canal de acompanhamento com farmacêutico",
                  "Análise de exames laboratoriais",
                  "Controle de aplicações injetáveis",
                  "Relatórios personalizados com IA clínica",
                ].map((f) => (
                  <li key={f} className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-gold mt-0.5 flex-none" /> {f}
                  </li>
                ))}
              </ul>
              <Link
                to="/cadastro?plan=premium"
                className="mt-8 inline-flex w-full items-center justify-center rounded-sm bg-gold text-primary px-4 py-3 text-sm font-bold uppercase tracking-wider hover:bg-gold/90 transition-colors"
                data-testid="plan-cta-premium"
              >
                Quero o Plano Premium
              </Link>
            </div>
          </div>
          <p className="mt-10 text-center text-xs text-primary-foreground/60 max-w-2xl mx-auto">
            Procedimentos e aplicações são individualizados por indicação clínica e cobrados à parte.
          </p>
        </div>
      </section>

      {/* PILARES DE MARCA */}
      <section className="py-20 md:py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-2xl mb-12">
            <div className="overline text-gold mb-4">Nossos pilares</div>
            <h2 className="brand-wordmark text-3xl md:text-4xl font-bold uppercase text-primary">
              Performance que se sustenta.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { t: "Confiança", d: "Segurança e responsabilidade em cada decisão clínica.", icon: Shield },
              { t: "Saúde integrativa", d: "Ciência, natureza e cuidado individualizado.", icon: Leaf },
              { t: "Acompanhamento humano", d: "Presentes em cada etapa — da consulta à dúvida do caminho.", icon: Handshake },
              { t: "Resultados", d: "Performance, equilíbrio e qualidade de vida sustentáveis.", icon: Target },
            ].map(({ t, d, icon: Icon }) => (
              <div key={t} className="border-l-2 border-gold pl-5" data-testid={`pillar-${t.toLowerCase().replace(/\s/g, "-")}`}>
                <Icon className="w-6 h-6 text-gold mb-4" />
                <h3 className="brand-wordmark uppercase text-lg font-semibold text-primary mb-2">{t}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-24 md:py-32 bg-secondary/40 border-y border-border">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-14">
            <div className="overline text-gold mb-4">Perguntas frequentes</div>
            <h2 className="brand-wordmark text-3xl md:text-5xl font-bold text-primary uppercase tracking-tight leading-[1.05]">
              Tire suas dúvidas.
            </h2>
          </div>
          <div className="space-y-3">
            {FAQ.map((item, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                className="w-full text-left bg-card border border-border rounded-sm px-6 py-5 hover:border-gold transition-colors"
                data-testid={`faq-item-${i}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <span className="font-heading font-semibold text-primary text-base">{item.q}</span>
                  <ChevronDown
                    className={`w-5 h-5 text-gold flex-none transition-transform ${openFaq === i ? "rotate-180" : ""}`}
                  />
                </div>
                {openFaq === i && (
                  <p className="mt-4 text-sm text-muted-foreground leading-relaxed" data-testid={`faq-answer-${i}`}>
                    {item.a}
                  </p>
                )}
              </button>
            ))}
          </div>
          <div className="mt-14 text-center">
            <p className="text-sm text-muted-foreground mb-4">Ainda com dúvidas?</p>
            <Link
              to="/agendamento"
              className="inline-flex items-center gap-2 rounded-sm bg-primary text-primary-foreground px-6 py-3 text-sm font-semibold hover:bg-primary/90 transition-colors"
              data-testid="faq-cta-agendar"
            >
              <MessageCircle className="w-4 h-4" /> Agendar Avaliação
            </Link>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-primary text-primary-foreground pt-16 pb-10">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 mb-12">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-9 h-9 rounded-sm bg-gold text-primary grid place-items-center">
                  <Leaf className="w-5 h-5" />
                </div>
                <div>
                  <div className="brand-wordmark font-bold text-sm uppercase">Centro de Performance</div>
                  <div className="brand-wordmark font-bold text-sm text-gold uppercase">Farmacêutica</div>
                </div>
              </div>
              <p className="text-xs text-primary-foreground/70 leading-relaxed max-w-xs">
                Cuidado clínico farmacêutico contínuo — metabolismo, intestino, farmacoterapia e
                monitoramento personalizado.
              </p>
            </div>
            <div>
              <div className="overline text-gold mb-4">Contato</div>
              <ul className="space-y-2 text-sm text-primary-foreground/80">
                <li data-testid="footer-whatsapp">WhatsApp: (94) 98417-2798</li>
                <li data-testid="footer-endereco">Atendimento presencial e digital</li>
              </ul>
            </div>
            <div>
              <div className="overline text-gold mb-4">Institucional</div>
              <ul className="space-y-2 text-sm text-primary-foreground/80">
                <li><Link to="/agendamento" className="hover:text-gold">Agendar Avaliação</Link></li>
                <li><a href="#planos" className="hover:text-gold">Planos de Acompanhamento</a></li>
                <li><a href="#faq" className="hover:text-gold">Perguntas Frequentes</a></li>
                <li><Link to="/login" className="hover:text-gold">Acesso do paciente</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-primary-foreground/15 pt-8">
            <p className="text-xs text-primary-foreground/60 leading-relaxed max-w-4xl" data-testid="footer-responsabilidade">
              <strong className="text-gold">Responsabilidade técnica:</strong> serviços prestados por farmacêuticos
              habilitados com registro ativo no CRF. As informações e conteúdos oferecidos têm caráter
              orientativo e de acompanhamento clínico farmacêutico, não substituindo a consulta médica quando indicada.
              O tratamento é individualizado conforme avaliação profissional.
            </p>
            <div className="mt-6 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-primary-foreground/60">
              <div>LGPD · Dados tratados conforme legislação vigente</div>
              <div>&copy; 2026 Centro de Performance Farmacêutica</div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
